import { useState } from 'react'
import { RichTextEditor } from './RichTextEditor'

interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  onSave?: () => void
  placeholder?: string
  showPreview?: boolean
}

export function MarkdownEditor({
  value,
  onChange,
  onSave,
  placeholder = "Markdownで議事録を編集してください...",
  showPreview = true
}: MarkdownEditorProps) {
  const [activeTab, setActiveTab] = useState<'edit' | 'preview' | 'split'>('edit')

  // 簡単なMarkdown to HTMLコンバーター
  const renderMarkdown = (markdown: string) => {
    return markdown
      // ヘッダー
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-gray-900 mt-6 mb-3">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold text-gray-900 mt-8 mb-4">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold text-gray-900 mt-8 mb-6">$1</h1>')
      // 太字
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      // イタリック
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      // リスト
      .replace(/^\- (.*$)/gim, '<li class="ml-4 mb-1">• $1</li>')
      .replace(/^\* (.*$)/gim, '<li class="ml-4 mb-1">• $1</li>')
      .replace(/^(\d+)\. (.*$)/gim, '<li class="ml-4 mb-1">$1. $2</li>')
      // 改行
      .replace(/\n/g, '<br>')
  }

  const TabButton = ({ label, isActive, onClick }: {
    label: string
    isActive: boolean
    onClick: () => void
  }) => (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
        isActive
          ? 'bg-white text-blue-600 border-b-2 border-blue-600'
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  )

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* タブヘッダー */}
      {showPreview && (
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex gap-1">
          <TabButton
            label="編集"
            isActive={activeTab === 'edit'}
            onClick={() => setActiveTab('edit')}
          />
          <TabButton
            label="プレビュー"
            isActive={activeTab === 'preview'}
            onClick={() => setActiveTab('preview')}
          />
          <TabButton
            label="分割表示"
            isActive={activeTab === 'split'}
            onClick={() => setActiveTab('split')}
          />
        </div>
      )}

      {/* コンテンツエリア */}
      <div className="bg-white">
        {activeTab === 'edit' && (
          <RichTextEditor
            value={value}
            onChange={onChange}
            onSave={onSave}
            placeholder={placeholder}
            className="border-0"
          />
        )}

        {activeTab === 'preview' && (
          <div className="p-4 min-h-[400px]">
            <div className="prose max-w-none">
              <div
                className="text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: renderMarkdown(value || '<p class="text-gray-400">プレビューするコンテンツがありません</p>')
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'split' && (
          <div className="flex h-[400px]">
            {/* 編集エリア */}
            <div className="w-1/2 border-r border-gray-200">
              <RichTextEditor
                value={value}
                onChange={onChange}
                onSave={onSave}
                placeholder={placeholder}
                className="border-0 h-full"
              />
            </div>
            
            {/* プレビューエリア */}
            <div className="w-1/2 p-4 overflow-y-auto">
              <div className="prose max-w-none">
                <div
                  className="text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(value || '<p class="text-gray-400">プレビューするコンテンツがありません</p>')
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* フッター情報 */}
      <div className="bg-gray-50 border-t border-gray-200 px-4 py-2 text-xs text-gray-500 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <span>文字数: {value.length}</span>
          <span>行数: {value.split('\n').length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span>Markdown対応</span>
          <span>•</span>
          <span>自動保存</span>
        </div>
      </div>
    </div>
  )
}
